import React, { useEffect, useMemo, useState } from 'react'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { useFormContext } from 'react-hook-form'
import { Autocomplete, AutocompleteRenderInputParams } from '@mui/material'

import TextField from '@material-ui/core/TextField'
import { get } from 'lodash'
import { Country, Maybe, useGetTransactionForbiddenCountriesLazyQuery } from '../../../graphql'
import { ControlledTextFieldProps, OptionType } from '../../../types'
import { CountriesList, CountriesUnknownList } from '../../../utils/Data'
import { filterOptionsAny, filterOptionsStart, handleLatinKeysDown } from '../../../utils'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { MobileSelector } from './MobileSelector'
import { BaseEnumWithKey } from './types'

type CountryAutocompleteSelectProps = {
  name: string
  defaultValue?: Maybe<string> | undefined
  clearFields?: () => void
  readOnly?: boolean | undefined
  setDisableParentScroll?: React.Dispatch<React.SetStateAction<boolean>>
  withUnknown?: boolean
  onlyLatinLetters?: boolean
  handleChange?: (value: string) => void
  filterByAny?: boolean
  excludeForbidden?: boolean
  filterOptions?: (options: BaseEnumWithKey[]) => BaseEnumWithKey[]
  exclusiveCountries?: string[]
}
const defaultRules = {} //{ required: true }

export const CountryAutocompleteSelect: React.FC<
  CountryAutocompleteSelectProps & Partial<ControlledTextFieldProps>
> = ({
  name = '',
  defaultValue = '',
  readOnly = false,
  clearFields,
  rules,
  setDisableParentScroll,
  withUnknown = false,
  onlyLatinLetters = true,
  handleChange,
  filterByAny = false,
  excludeForbidden = false,
  filterOptions,
  exclusiveCountries,
  ...rest
}) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [defaultVal, setDefaultVal] = useState()
  const { errors, register, setValue, watch, getValues } = useFormContext()
  const error = errors ? get(errors, name || 'FormAutocompleteSelect') : null
  const formPreSettedVal = watch(name)

  const [
    GetTransactionForbiddenCountriesQuery,
    { data },
  ] = useGetTransactionForbiddenCountriesLazyQuery()

  useEffect(() => {
    if (!getValues(name)) {
      register(name, { ...defaultRules, ...(rules ?? {}) })
    }
  }, [register, getValues, name, rules])

  useEffect(() => {
    setDefaultVal(formPreSettedVal)
  }, [formPreSettedVal])

  const options = useMemo(() => {
    if (!excludeForbidden) {
      const opts = (withUnknown ? CountriesUnknownList : CountriesList).filter(
        (v) => !exclusiveCountries?.length || exclusiveCountries.includes(v.key.toUpperCase()),
      )
      return filterOptions ? filterOptions(opts) : opts
    } else {
      ;(async () => {
        await GetTransactionForbiddenCountriesQuery()
      })()
    }
    const opts = CountriesList.filter(
      (v) =>
        !data?.transactionForbiddenCountries?.includes(v.key as Country) &&
        (!exclusiveCountries?.length || exclusiveCountries.includes(v.key.toUpperCase())),
    )
    return filterOptions ? filterOptions(opts) : opts
  }, [excludeForbidden, withUnknown, data, exclusiveCountries])

  const selectedOption = useMemo(() => options.find(({ key }) => key === formPreSettedVal), [
    formPreSettedVal,
    options,
  ])

  const handleChangeOption = (selectedValue: string | null) => {
    if (clearFields) {
      clearFields()
    }
    setValue(name, selectedValue || defaultValue, {
      shouldValidate: true,
      shouldDirty: true,
    })
    if (selectedValue && handleChange) {
      handleChange(selectedValue)
    }
  }

  return !isSmallScreen ? (
    <Autocomplete
      key={`${defaultVal}+${name}`}
      sx={
        readOnly
          ? {
              '& .MuiFormLabel-root': { zIndex: 1 },
              '& div': {
                backgroundColor: '#F5F5F5',
                pointerEvents: 'none',
              },
              '& .MuiIconButton-root, & .MuiInputBase-root': {
                color: '#999999',
              },
            }
          : undefined
      }
      options={options}
      onOpen={() => setDisableParentScroll && setDisableParentScroll(true)}
      onClose={() => setDisableParentScroll && setDisableParentScroll(false)}
      getOptionLabel={(option) => (option as BaseEnumWithKey).label}
      value={selectedOption}
      onChange={(_, option) => handleChangeOption((option as BaseEnumWithKey)?.key)}
      defaultValue={options?.find((v: BaseEnumWithKey) => v.key === defaultVal)}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          name={name}
          {...params}
          error={!!error}
          helperText={error ? error.message : null}
          {...rest}
          disabled={readOnly}
          onKeyDown={(e) => handleLatinKeysDown(e, onlyLatinLetters)}
        />
      )}
      filterOptions={filterByAny ? filterOptionsAny : filterOptionsStart}
      openText={''}
      closeText={''}
      popupIcon={<ExpandMoreIcon />}
      disabled={readOnly}
      disableClearable
      autoHighlight
      clearOnEscape
      openOnFocus={!readOnly}
    />
  ) : (
    <MobileSelector
      name={name}
      options={options as OptionType[]}
      handleChange={handleChangeOption}
      readOnly={readOnly}
      {...rest}
    />
  )
}
